// hooks/order/useOrderList.js
import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL, PAGE_SIZES, DEFAULT_PAGE_SIZE, API_ENDPOINTS } from "@/utils/order/constants";
import { buildApiUrl } from "@/utils/order/helpers";

export function useOrderList() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        offset: 0,
        limit: DEFAULT_PAGE_SIZE,
        hasMore: true,
        total: 0
    });

    // Lấy danh sách đơn hàng
    const fetchOrders = useCallback(async (offset = 0, limit = DEFAULT_PAGE_SIZE, append = false, params = {}) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError(null);

        // Nếu có flag _clearData, xóa dữ liệu orders hiện tại
        if (params._clearData) {
            setOrders([]);
            delete params._clearData; // Xóa flag để không ảnh hưởng đến API call
        }

        try {
            const token = localStorage.getItem("authToken"); // Lấy token từ localStorage

            // Tính toán page từ offset/limit
            const page = Math.floor(offset / limit) + 1;
            
            const {
                status,
                start_date,
                end_date,
                search,
            } = params;

            // Xây dựng query params
            const queryParams = new URLSearchParams();
            queryParams.append("page", page);
            queryParams.append("limit", limit);

            if (status) queryParams.append("status", status);
            if (start_date)
                queryParams.append("start_date", start_date.toISOString());
            if (end_date)
                queryParams.append("end_date", end_date.toISOString());
            if (search) queryParams.append("search", search);

            console.log(`Gọi API list với tham số: ${queryParams.toString()}`);

            // Sử dụng hàm tiện ích buildApiUrl
            const apiUrl = `${API_BASE_URL}${buildApiUrl(API_ENDPOINTS.LIST, {}, queryParams)}`;
            
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("List API response:", response.data);

            // API thực tế trả về isSuccess
            if (response.data.isSuccess) {
                // Lấy dữ liệu orders từ response.data.data
                const ordersData = response.data.data;
                
                if (!Array.isArray(ordersData)) {
                    console.error("Kết quả trả về không phải là mảng:", ordersData);
                    setError("Định dạng dữ liệu không hợp lệ");
                    return;
                }

                // Nếu là append, thêm vào danh sách hiện tại
                if (append) {
                    setOrders(prevOrders => [...prevOrders, ...ordersData]);
                } else {
                    setOrders(ordersData);
                }
                
                // Cập nhật pagination từ dữ liệu trả về
                const { pagination: paginationData } = response.data;
                const hasMore = paginationData.hasMore !== undefined 
                    ? paginationData.hasMore 
                    : (paginationData.page < paginationData.totalPages);
                
                // Cập nhật pagination state
                setPagination({
                    offset: append ? offset + limit : limit,
                    limit,
                    hasMore,
                    total: paginationData.total || 0
                });
            } else {
                setError(response.data.error?.message || "Không thể tải danh sách đơn hàng");
            }
        } catch (err) {
            setError(err.message || "Không thể tải danh sách đơn hàng");
            console.error("Error fetching orders:", err);
        } finally {
            if (append) {
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, []);

    // Tìm kiếm đơn hàng
    const searchOrders = useCallback(async (searchTerm, offset = 0, limit = DEFAULT_PAGE_SIZE, append = false, params = {}) => {
        if (append) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError(null);

        // Nếu có flag _clearData, xóa dữ liệu orders hiện tại
        if (params._clearData) {
            setOrders([]);
            delete params._clearData; // Xóa flag để không ảnh hưởng đến API call
        }

        try {
            const token = localStorage.getItem("authToken");

            // Tính toán page từ offset/limit
            const page = Math.floor(offset / limit) + 1;
            
            // Xây dựng query params
            const queryParams = new URLSearchParams();
            queryParams.append("searchTerm", searchTerm);
            queryParams.append("page", page);
            queryParams.append("limit", limit);

            // Thêm các tham số bổ sung
            if (params.status) queryParams.append("status", params.status);
            if (params.sortBy) queryParams.append("sortBy", params.sortBy);
            if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

            console.log(`Gọi API search với tham số: ${queryParams.toString()}`);

            // Sử dụng hàm tiện ích buildApiUrl
            const apiUrl = `${API_BASE_URL}${buildApiUrl(API_ENDPOINTS.SEARCH, {}, queryParams)}`;
            
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log("Search API response:", response.data);

            // API thực tế trả về isSuccess thay vì success
            if (response.data.isSuccess) {
                // Lấy dữ liệu orders từ response.data.data
                const ordersData = response.data.data;
                
                if (!Array.isArray(ordersData)) {
                    console.error("Kết quả trả về không phải là mảng:", ordersData);
                    setError("Định dạng dữ liệu không hợp lệ");
                    return;
                }

                // Nếu là append, thêm vào danh sách hiện tại
                if (append) {
                    setOrders(prevOrders => [...prevOrders, ...ordersData]);
                } else {
                    setOrders(ordersData);
                }
                
                // Cập nhật pagination từ dữ liệu trả về
                const { pagination: paginationData } = response.data;
                setPagination({
                    offset: append ? offset + limit : limit,
                    limit,
                    hasMore: paginationData.hasMore,
                    total: paginationData.total || 0
                });
            } else {
                setError(response.data.error?.message || "Không thể tìm kiếm đơn hàng");
            }
        } catch (err) {
            setError(err.message || "Không thể tìm kiếm đơn hàng");
            console.error("Error searching orders:", err);
        } finally {
            if (append) {
                setLoadingMore(false);
            } else {
                setLoading(false);
            }
        }
    }, []);

    // Cập nhật trạng thái đơn hàng
    const updateOrderStatus = useCallback(async (orderId, newStatus) => {
        setLoading(true);

        try {
            const token = localStorage.getItem("authToken");
            
            // Sử dụng hàm tiện ích buildApiUrl
            const apiUrl = `${API_BASE_URL}${buildApiUrl(API_ENDPOINTS.UPDATE_STATUS, { id: orderId })}`;

            const response = await axios.post(
                apiUrl,
                { status: newStatus },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            console.log("Update status response:", response.data);
            
            if (response.data.isSuccess) {
                // Cập nhật lại danh sách đơn hàng sau khi thay đổi trạng thái
                setOrders((prevOrders) =>
                    prevOrders.map((order) => {
                        // Xử lý dựa trên id
                        if (order.id === orderId) {
                            return { 
                                ...order, 
                                order_status: newStatus
                            };
                        }
                        return order;
                    })
                );

                return true;
            } else {
                setError(response.data.error?.message || "order.updateError");
                return false;
            }
        } catch (err) {
            setError("order.updateError");
            console.error("Error updating order status:", err);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        orders,
        loading,
        loadingMore,
        error,
        pagination,
        setPagination,
        fetchOrders,
        searchOrders,
        updateOrderStatus,
    };
}
